import { useState, useEffect, useCallback, useRef } from "react";
import { souschefChat, getSession } from "@/lib/atlasApi";

const SESSION_KEY = "souschef_session";
const GREETING = {
  role: "assistant",
  text: "Sou o **SousChef AI**. Posso calcular sinergia molecular, sugerir substitutos químicos e cruzar o acervo científico com o grafo de sabores. Pergunte algo, ex: _\"Com o que o tomate combina e por quê?\"_",
};

function ingredientsFromGraph(gc) {
  const nodes = gc?.nodes || [];
  return nodes
    .filter((n) => (n.labels || []).includes("Ingredient") && n.properties?.sop_raft_id)
    .map((n) => ({ sop_raft_id: n.properties.sop_raft_id, name: n.properties.name }));
}

export function useSouschef({ onGraphContext } = {}) {
  const [messages, setMessages] = useState([GREETING]);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [lastMeta, setLastMeta] = useState(null);
  const hydratedFor = useRef(null);

  const hydrate = useCallback(async (id) => {
    try {
      const doc = await getSession(id);
      const mapped = (doc.messages || []).map((m) => ({
        role: m.role,
        text: m.content,
        sources: m.sources || [],
        tools: (m.tool_calls || []).map((t) => t.name),
      }));
      setMessages(mapped.length ? mapped : [GREETING]);
    } catch {
      setMessages([GREETING]);
    }
  }, []);

  useEffect(() => {
    if (sessionId && hydratedFor.current !== sessionId) {
      hydratedFor.current = sessionId;
      hydrate(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const send = useCallback(async (text) => {
    const question = (text || "").trim();
    if (!question || busy) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const res = await souschefChat(question, sessionId);
      if (res.session_id && res.session_id !== sessionId) {
        hydratedFor.current = res.session_id;
        setSessionId(res.session_id);
        localStorage.setItem(SESSION_KEY, res.session_id);
      }
      const gc = res.graph_context || {};
      if (onGraphContext) onGraphContext(gc);
      setLastMeta({
        tool_calls: res.tool_calls || [],
        sources: res.sources || [],
        graph_context: gc,
        auto_synced: !!res.auto_synced,
        synergy_index: res.synergy_index,
        cost_estimate: res.cost_estimate || null,
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: res.answer,
          sources: res.sources || [],
          entities: gc.entities_matched || [],
          tools: (res.tool_calls || []).map((t) => t.name),
          ingredients: ingredientsFromGraph(gc),
          autoSynced: !!res.auto_synced,
          synergyIndex: res.synergy_index,
          costEstimate: res.cost_estimate || null,
        },
      ]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Erro ao consultar o SousChef AI.", error: true }]);
    } finally {
      setBusy(false);
    }
  }, [busy, sessionId, onGraphContext]);

  const newSession = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    hydratedFor.current = null;
    setSessionId(null);
    setLastMeta(null);
    setMessages([GREETING]);
  }, []);

  const loadSession = useCallback((id) => {
    localStorage.setItem(SESSION_KEY, id);
    hydratedFor.current = id;
    setSessionId(id);
    setLastMeta(null);
    hydrate(id);
  }, [hydrate]);

  return { messages, busy, sessionId, lastMeta, send, newSession, loadSession };
}
